import { useEffect, useState } from "react";
import {
  BasicDropdown,
  Button,
  Size,
  ModalDialog,
  useToast,
} from "@laerdal/life-react-components";
import TimePicker from "./TimePicker";
import {
  LocalStorageKey,
  removeKeyFromLocalStorage,
  useLocalStorage,
} from "../helpers/LocalStorage";
import { resetCalculatorDataState } from "../helpers/StateManager";
import SlowProgressCalculation from "../helpers/SlowProgressCalculation";
import DropDownDatasources from "../helpers/DropDownDatasources";
import Global from "../helpers/Global";
import { ToastMessageType } from "../helpers/Enums";

const Calculator = () => {
  // Variables
  const [slowProgressTime, setSlowProgressTime] = useLocalStorage(
    LocalStorageKey.SlowProgressTime,
    "--:--"
  );
  const [registeredLatestTime, setRegisteredLatestTime] = useLocalStorage(
    LocalStorageKey.RegisteredLatestTime,
    ""
  );
  const [selectedLatestTime, setSelectedLatestTime] =
    useState(registeredLatestTime);
  const [registeredCervixOpening, setRegisteredCervixOpening] = useLocalStorage(
    LocalStorageKey.RegisteredCervixOpening,
    ""
  );
  const [selectedCervixOpening, setSelectedCervixOpening] = useState(
    registeredCervixOpening
  );
  const [addedHoursToSlowProgress, setAddedHoursToSlowProgress] =
    useLocalStorage(LocalStorageKey.AddedHoursToSlowProgress, "");
  const [removeFocusFromTimeInput, setRemoveFocusFromTimeInput] =
    useState(false);
  const [currentOpenToastId, setCurrentOpenToastId] = useState("");
  const [openTidspunktConfirmationModal, setOpenTidspunktConfirmationModal] =
    useState(false);
  const cervixOpeningValues = DropDownDatasources.getCervixOpeningValues(false);

  // Global state managers
  const [resetCalculatorData, setResetCalculatorData] =
    resetCalculatorDataState();

  // Hooks
  const { addToast, removeToast } = useToast();

  // Functions

  const SelectCervixOpening = (value: string) => {
    setSelectedCervixOpening(value);
  };

  const ExecuteSlowProgressTimeCalculation = () => {
    const [slowProgressValue, addedHours] =
      SlowProgressCalculation.calculateCurrentSlowProgressValue(
        selectedCervixOpening,
        selectedLatestTime
      );

    // Setting the slow progress time value
    setRegisteredCervixOpening(selectedCervixOpening);
    setRegisteredLatestTime(selectedLatestTime);
    setSlowProgressTime(slowProgressValue);
    setAddedHoursToSlowProgress(addedHours);

    // Removing the focus on the time input field since we need the app to
    // update the current time after doing the calculation
    setRemoveFocusFromTimeInput(true);

    // Showing successful message box
    return Global.showMessageBox(
      addToast,
      "Tidspunkt for langsom fremgang er endret"
    );
  };

  const CalculateDelayedProgressTimeValue = () => {
    let toastId = "";

    // Removing the toast with the id that has been stored into the state. This will avoid having several
    // toast windows in the screen since it might confuse the user, but instead only showing one at the time
    removeToast(currentOpenToastId);

    const validationResults =
      SlowProgressCalculation.validateInputsBeforeCalculation(
        selectedCervixOpening,
        registeredCervixOpening,
        selectedLatestTime,
        registeredLatestTime
      );

    if (!validationResults) {
      toastId = ExecuteSlowProgressTimeCalculation();
    } else {
      // Show a Toast with the error/warning/info message
      switch (validationResults.messageType) {
        case ToastMessageType.Info:
        case ToastMessageType.Warning:
        case ToastMessageType.Error:
          toastId = Global.showMessageBox(
            addToast,
            validationResults.message,
            validationResults.messageType
          );
          break;
        case "tidspunkt-confirmation":
          setOpenTidspunktConfirmationModal(true);
          break;
      }
    }

    // Set current toast Id in the state
    setCurrentOpenToastId(toastId);
  };

  // Close the tidspunkt confirmation modal window
  const CloseTidspunktModalWindow = () => {
    setOpenTidspunktConfirmationModal(false);
  };

  // Confirmation modal window buttons
  const GetTidspunktModalConfirmationButtonOneLabel = () => {
    return (
      <span className="tidspunkt-confirmation-window-button-text">
        {registeredCervixOpening} cm målt {registeredLatestTime} →
        <span className="slow-progress-time"> {slowProgressTime}</span>
      </span>
    );
  };

  const GetTidspunktModalConfirmationButtonTwoLabel = () => {
    // Second button contains info for the calculation of the slow progress with the new opening and time.
    const [newPossibleSlowProgressValue] =
      SlowProgressCalculation.calculateCurrentSlowProgressValue(
        selectedCervixOpening,
        selectedLatestTime
      );
    return (
      <span className="tidspunkt-confirmation-window-button-text">
        {selectedCervixOpening} cm målt {selectedLatestTime} →
        <span className="slow-progress-time">
          {" "}
          {newPossibleSlowProgressValue}
        </span>
      </span>
    );
  };

  const SetNewSlowProgressCalculatedValues = () => {
    let toastId = ExecuteSlowProgressTimeCalculation();
    // Set current toast Id in the state
    setCurrentOpenToastId(toastId);
    setOpenTidspunktConfirmationModal(false);
  };

  useEffect(() => {
    // if the reset button (in the action bar component) is triggered, then this block of code
    // where the reset data for the calculator happens, will be executed
    if (resetCalculatorData) {
      // Removing keys from local storage
      // INFO: Not using localStorage.clear() since it does not clear cached values in local storage
      // and values comes back randomly when refreshing page or resetting new patient
      removeKeyFromLocalStorage(LocalStorageKey.SlowProgressTime);
      removeKeyFromLocalStorage(LocalStorageKey.RegisteredLatestTime);
      removeKeyFromLocalStorage(LocalStorageKey.RegisteredCervixOpening);
      removeKeyFromLocalStorage(LocalStorageKey.InputWithFocusTimestamp);
      removeKeyFromLocalStorage(LocalStorageKey.AddedHoursToSlowProgress);

      // Resetting variables
      setSelectedCervixOpening("");
      setSelectedLatestTime("");
      setSlowProgressTime("--:--");
      setRegisteredCervixOpening("");
      setRegisteredLatestTime("");
      setAddedHoursToSlowProgress("");
      setRemoveFocusFromTimeInput(true);

      // Removing the toast with the id that has been stored into the state. This will avoid having several
      // toast windows in the screen since it might confuse the user, but instead only showing one at the time
      removeToast(currentOpenToastId);

      // Setting back the resetCalculatorData (in the parent component) to false value, to let the parent component
      // know that the resetting of the data has happened
      setResetCalculatorData(false);
    }
  }, [
    resetCalculatorData,
    currentOpenToastId,
    removeToast,
    setAddedHoursToSlowProgress,
    setRegisteredCervixOpening,
    setRegisteredLatestTime,
    setSlowProgressTime,
    setResetCalculatorData,
  ]);

  return (
    <>
      <div className="calculator-container">
        <div className="main-calculator-input-container">
          <div className="main-input-container">
            <div className="input-container">
              <span className="input-title">Mormunnsåpning</span>
              <BasicDropdown
                id="basicDropdown"
                list={cervixOpeningValues}
                value={selectedCervixOpening}
                size={Size.Small}
                onSelect={(value) => SelectCervixOpening(value)}
                minWidth="230px"
              />
            </div>
            <div className="input-container">
              <span className="input-title">
                Tidspunkt for vaginal undersøkelse
              </span>
              <TimePicker
                removeFocusFromTimeInput={removeFocusFromTimeInput}
                setRemoveFocusInParentComponent={setRemoveFocusFromTimeInput}
                setSelectedLatestTime={setSelectedLatestTime}
              />
            </div>
          </div>
          <div className="button-container">
            <Button
              size={Size.Medium}
              minWidth="230px"
              onClick={() => CalculateDelayedProgressTimeValue()}
            >
              Vis tidspunkt
            </Button>
          </div>
        </div>
        <div className="calculator-result-container">
          <div className="main-title">Tidspunkt for langsom fremgang</div>
          <div className="delayed-progress-time-value">{slowProgressTime}</div>
          {slowProgressTime !== "--:--" && (
            <div className="more-details">
              <div className="first-line">
                <span className="cervix-opening-value">
                  {addedHoursToSlowProgress || "-"} timer
                </span>
                {" etter "}
                <span className="cervix-opening-value">
                  {registeredCervixOpening || "-"} cm
                </span>
                {" ble "}
              </div>
              <div className="second-line">
                {"målt første gang "}
                <span className="latest-time-registered-value">
                  {registeredLatestTime || "--:--"}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="container-separator"></div>
      </div>
      <ModalDialog
        key="ConfirmationModalDialog"
        title="Tidspunkt for langsom fremgang ikke definert"
        size={Size.Medium}
        isModalOpen={openTidspunktConfirmationModal}
        closeAction={() => {
          CloseTidspunktModalWindow();
        }}
        closeModalAndClearInput={() => {}}
        submitAction={() => {}}
        buttons={[
          {
            action: () => {
              CloseTidspunktModalWindow();
            },
            variant: "secondary",
            children: GetTidspunktModalConfirmationButtonOneLabel(),
          },
          {
            action: () => {
              SetNewSlowProgressCalculatedValues();
            },
            variant: "secondary",
            children: GetTidspunktModalConfirmationButtonTwoLabel(),
          },
        ]}
      >
        <span className="tidskpunt-confirmation-window-body">
          WHOs Labour Care Guide definerer ikke tidspunkt for langsom fremgang
          når mormunnsåpningen blir mindre over tid. Hvilken kombinasjon av
          mormunnsåpning og tidspunkt vil du bruke?
        </span>
      </ModalDialog>
    </>
  );
};

export default Calculator;
